import ReactGA4 from 'react-ga4';
import {ColorMode} from '../colorMode/ColorMode.enum';
import {LOCAL_STORAGE_COLOR_MODE_KEY} from '../colorMode/LocalStorageColorModeKey.constant';
import {getEnv} from '../common/env/env';
import {ServerEnv} from '../common/env/ServerEnv.enum';
import {requestContext} from '../common/requestContext';
import {IS_CLIENT_SIDE} from '../common/shared/utils/constants';
import {initGoogleAnalytics} from './initGoogleAnalytics';
import {initTiara} from './initTiara';
import {
  TrackEventObject,
  UsageScrollObject,
} from './TrackEventObject.interface';
import {
  currentEventContext,
  currentSearchActionEventContext,
  prevEvents,
} from './variables';

export const lastLoggingPageviewContext: {
  obj?: TrackEventObject;
  loggingTime?: Date;
  scrollObj: UsageScrollObject;
} = {scrollObj: {}};

export async function trackEvent(
  obj: TrackEventObject,
  // 페이지웹이 아닌 외부페이지에서의 로깅 요청 여부
  isExternalPage?: boolean
) {
  if (!IS_CLIENT_SIDE) {
    return;
  }
  const isWebView = requestContext.requestInfo.display.isWebView;
  // ViewImp 를 즉각적으로 보내기 위해 하나씩 세팅하지만 실제 전송할 데이터는 array 형태로 되어있어야합니다.
  const transferObj = {
    ...obj,
    viewimp_contents: Array.isArray(obj.viewimp_contents)
      ? isWebView
        ? obj.viewimp_contents[0]
        : obj.viewimp_contents
      : isWebView
        ? obj.viewimp_contents
        : [obj.viewimp_contents],
  };

  if (transferObj.customProps) {
    transferObj.customProps.islogin = requestContext.requestInfo.user
      ? 'y'
      : 'n';
    const currentColorMode = window.localStorage?.getItem(
      LOCAL_STORAGE_COLOR_MODE_KEY
    ) as ColorMode;

    transferObj.customProps.display_theme =
      currentColorMode === ColorMode.System
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? '다크모드'
          : '라이트모드'
        : currentColorMode === ColorMode.Dark
          ? '다크모드'
          : '라이트모드';
    transferObj.customProps.user_home =
      requestContext.requestInfo.appConfig?.tastesHome ?? null;
    transferObj.customProps.user_favorite_genre =
      requestContext.requestInfo.appConfig?.tastesGenre ?? null;
  }

  // 빌링에서 충전 완료 이벤트를 남기게 되면 어떤 경로로 캐시충전을 하게 되었는지 정보를 같이 남겨줍니다.
  if (transferObj.actionName === '충전_완료') {
    transferObj.customProps = {
      ...transferObj.customProps,
      ...currentEventContext.cashChargeInfo,
    };
    currentEventContext.cashChargeInfo = undefined;
  }

  window.KPApp?.trackEvent
    ? window.KPApp.trackEvent(JSON.stringify(transferObj))
    : // @ts-ignore
      await send(transferObj);

  // 앱내외 할 것 없이 웹이라면 GA 로그를 보냅니다.
  sendToGoogleAnalytics(transferObj);

  // 이벤트 전송 후 추후 참조하기 위해 각종 정보 취합(페이지뷰, 열람전환로그 등)

  // 열람전환로그(작품 클릭 시 생성)
  if (obj.actionName === '작품_클릭') {
    currentEventContext.viewerConversionInfo = {
      last_click_page: currentEventContext.currentPageView?.page,
      last_click_section: currentEventContext.currentPageView?.section,
      last_click_page_id: currentEventContext.currentPageView?.page_meta?.id,
      last_click_page_type:
        currentEventContext.currentPageView?.page_meta?.type,
      last_click_series_id:
        currentEventContext.currentPageView?.page_meta?.series_id ||
        obj.eventMeta?.series_id,
      last_click_layer1: obj.click?.layer1,
      last_click_ordnum: obj.click?.ordnum,
    };
    currentEventContext.helixConversionInfo = {
      ...(obj.customProps.helix_yn === 'y'
        ? {
            helix_seed: obj.customProps.helix_seed,
            helix_yn: obj.customProps.helix_yn,
            helix_id: obj.customProps.helix_id,
          }
        : {}),
    };
  } else if (obj.actionName === '회차_클릭') {
    if (
      currentEventContext.currentPageView?.page_meta?.type === 'screenId' ||
      currentEventContext.currentPageView?.page_meta?.type === 'reference' ||
      currentEventContext.currentPageView?.section === '보관함'
    ) {
      currentEventContext.viewerConversionInfo = {
        last_click_section: currentEventContext.currentPageView?.section,
        last_click_page: currentEventContext.currentPageView?.page,
        last_click_page_id: currentEventContext.currentPageView?.page_meta?.id,
        last_click_page_type:
          currentEventContext.currentPageView?.page_meta?.type,
        last_click_series_id:
          currentEventContext.currentPageView?.page_meta?.series_id,
        last_click_layer1: obj.click?.layer1,
        last_click_ordnum: obj.click?.ordnum,
      };
    }
  }

  if (obj.actionKind === 'Search') {
    if (obj.search?.search_type) {
      currentSearchActionEventContext.lastSearchType = obj.search.search_type;
    }
  }

  // TrackPageViewContainer 에서 currentPageView 를 남기지만 trackEvent 는 빌링웹 등 외부에서 호출할 수 있는 함수라 이곳에서도 남깁니다
  if (obj.actionType === 'Pageview') {
    // 외부페이지가 아닌 경우에는 currentPageView를 다시 한번 설정해줍니다.
    if (!isExternalPage) {
      currentEventContext.currentPageView = obj;
    }
    // 현재 콘텐츠홈을 이탈한 경우 열람전환로그를 리셋합니다. 다음 케이스엔 리셋하지 않습니다
    // - 뷰어 : 뷰어_오픈 내에서 열람전환로그 생성 후 리셋
    // - 콘텐츠홈 : 열람전환로그는 콘텐츠홈 내에선 유지/변경됨
    // - 댓글 : 콘텐츠홈, 뷰어에서 접근 가능한 댓글 페이지는 열람전환로그를 유지
    if (
      currentEventContext.currentPageView?.section &&
      currentEventContext.currentPageView.section !== '뷰어' &&
      currentEventContext.currentPageView.section !== '콘텐츠홈' &&
      currentEventContext.currentPageView.section !== '댓글' &&
      currentEventContext.currentPageView.section !== '이용권충전'
    ) {
      if (currentEventContext.viewerConversionInfo?.last_click_page_id) {
        currentEventContext.viewerConversionInfo = undefined;
      }
      currentEventContext.helixConversionInfo = undefined;
    }
  }

  // Usage, ViewImp Logging은 직전 로깅정보로 사용하지 않으므로 이를 제외하고 나머지 이벤트만 저장합니다.
  if (obj.actionType !== 'Usage' && obj.actionType !== 'ViewImp') {
    prevEvents.push(obj);
  }
}

function send({
  section,
  page,
  actionType,
  actionName,
  actionKind,
  page_meta,
  search,
  ecommerce,
  viewimp_contents,
  eventMeta,
  click,
  usage,
  customProps,
}: TrackEventObject): Promise<void> {
  return initTiara().then(() => {
    const instance = window.TiaraTracker.getInstance()
      .setReferrer(document.referrer) // TODO: 티아라 가이드대로 넣긴 했는데 페이지 로그 가이드에도 넣어야하는지 확인
      .setSection(section)
      .setPage(page)
      .setPageMeta(null)
      .setAdTrackId('4144807185840030696');

    // XXX: 해당 필드가 있을때만 넣습니다. builder 패턴이라 if 도배되어있는데 더 좋은 방법이 있다면 고쳐도 좋을 것 같습니다
    if (page_meta) {
      instance.pageMeta(page_meta);
    }
    if (viewimp_contents) {
      instance.viewImpContents(viewimp_contents);
    }
    if (usage) {
      instance.usage(usage);
    }
    if (search) {
      instance.search({
        ...search,
        ...(actionName === '검색_완료' &&
          currentSearchActionEventContext.lastSearchType && {
            search_type: currentSearchActionEventContext.lastSearchType,
          }),
      });
    }
    if (click) {
      instance.click(click);
    }
    if (eventMeta) {
      instance.eventMeta(eventMeta);
    }
    if (ecommerce) {
      instance.ecommerce(ecommerce);
    }

    if (requestContext.requestInfo.user) {
      instance
        .setAppUserId(requestContext.requestInfo.user.appUserId)
        .setKakaoAppKey(getEnv().kakaoSdkId);

      instance.userExAccount({
        user_id: requestContext.requestInfo.user.uid,
      });
      instance.setThirdProvideAgree(true);
      instance.setThirdAdAgree(
        !!requestContext.requestInfo.appConfig?.marketingAgreement
      );
    }

    // 데이터팀에서 비로그인 식별을 위해 추가 요청한 키
    instance.userExAnonymous({
      device_id: requestContext.requestInfo.deviceId,
      identifier_id: requestContext.requestInfo.identifierId,
    });

    if (customProps) {
      let updatedCustomProps = customProps;

      if (customProps.user_action === 'pv' && prevEvents.length > 0) {
        // pv 면서 이전 로그가 있으면 before_XXX 를 보냅니다 (로그 문서 참고)
        const beforeEventLog = prevEvents[prevEvents.length - 1];
        updatedCustomProps = {
          ...updatedCustomProps,
          before_page_name: beforeEventLog.page,
          before_section_type: beforeEventLog.customProps?.section_type,
          before_section_id: beforeEventLog.customProps?.section_id,
          before_section_name: beforeEventLog.section,
          before_click_name: beforeEventLog.actionName,
        };
      }

      updatedCustomProps = {
        ...updatedCustomProps,
        web_version: getEnv().version,
        device_language: 'ko',
        ...(requestContext.requestInfo.user && {
          service_language: 'ko',
          country_code: 'kr',
        }),
      };

      instance.customProps(updatedCustomProps);
    }

    if (actionType === 'Pageview') {
      instance.trackPage(actionName).actionKind(actionKind).track();
    } else if (actionType === 'Event') {
      const track = instance.trackEvent(actionName);
      if (actionKind) {
        track.actionKind(actionKind);
      }
      track.track();
    } else if (actionType === 'ViewImp') {
      instance.trackViewImp(actionName).track();
    } else if (actionType === 'Usage') {
      const track = instance.trackEvent(actionName);
      // usage 사용시 UsagePage 필수
      track.actionKind('UsagePage');
      track.trackUsage(actionName).track();
    }
  });
}

function sendToGoogleAnalytics({
  page,
  actionType,
  actionName: _actionName,
  eventMeta,
  click,
}: TrackEventObject) {
  initGoogleAnalytics();

  // 현재 GA tracker는 리얼을 위한 한가지 밖에 없으므로, 개발 환경일 때 actionName 앞에 환경을 붙여 실 데이터와 구분하게 합니다.
  // 추후 tracking 환경이 추가되면 개발/리얼 환경을 분리시키는게 좋겠습니다.
  const env = getEnv().envName;
  const actionName =
    env === ServerEnv.Real ? _actionName : `[${env}]${_actionName}`;

  if (actionType === 'Pageview') {
    ReactGA4.send({
      hitType: 'pageview',
      page: `${window.location.pathname}${window.location.search}`,
      title: actionName,
    });
  } else if (actionType === 'Event') {
    ReactGA4.event({
      category: page,
      action: actionName,
      label: eventMeta?.id
        ? eventMeta.id
        : click?.layer1
          ? click.layer1
          : undefined,
    });
  }
}
